import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Search,
  Loader2,
  X,
  Package,
  Tag,
  FolderTree
} from 'lucide-react';
import toast from 'react-hot-toast';
import { productosApi, parametrosApi } from '../../utils/api';
import CabysExplorer from '../../components/CabysExplorer';

const UNIDADES_MEDIDA = [
  { value: 'Unid', label: 'Unidad' },
  { value: 'Kg', label: 'Kilogramo' },
  { value: 'g', label: 'Gramo' },
  { value: 'L', label: 'Litro' },
  { value: 'mL', label: 'Mililitro' },
  { value: 'm', label: 'Metro' },
  { value: 'cm', label: 'Centímetro' },
  { value: 'm2', label: 'Metro cuadrado' },
  { value: 'm3', label: 'Metro cúbico' },
  { value: 'Sp', label: 'Servicio Profesional' },
  { value: 'Os', label: 'Otros Servicios' },
  { value: 'h', label: 'Hora' },
  { value: 'd', label: 'Día' },
];

const TARIFAS_IVA = [
  { value: 0, label: 'Exento (0%)', codigo: '01' },
  { value: 1, label: '1%', codigo: '02' },
  { value: 2, label: '2%', codigo: '03' },
  { value: 4, label: '4%', codigo: '04' },
  { value: 8, label: '8%', codigo: '05' },
  { value: 13, label: '13% (General)', codigo: '08' },
];

export default function FormularioProducto({
  isModal = false,
  onSuccess = null,
  onCancel = null,
  initialNombre = ''
}) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = !isModal && !!id;
  const copyFromId = !isModal && !id ? searchParams.get('copyFrom') : null;
  const cabysInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing || !!copyFromId);
  const [errors, setErrors] = useState({});

  // CABYS search state
  const [cabysSearch, setCabysSearch] = useState('');
  const [cabysResults, setCabysResults] = useState([]);
  const [searchingCabys, setSearchingCabys] = useState(false);
  const [showCabysDropdown, setShowCabysDropdown] = useState(false);
  const [showCabysExplorer, setShowCabysExplorer] = useState(false);

  // Impuestos disponibles y seleccionados
  const [impuestosDisponibles, setImpuestosDisponibles] = useState([]);
  const [impuestosSeleccionados, setImpuestosSeleccionados] = useState([]);

  const [producto, setProducto] = useState({
    codigo: '',
    nombre: initialNombre,
    descripcion: '',
    codigoCabys: '',
    descripcionCabys: '',
    unidadMedida: 'Unid',
    moneda: 'USD',
    precio: '',
    tarifaIva: 13,
    codigoTarifaIva: '08',
    esServicio: false,
    activo: true
  });

  // Cargar impuestos disponibles al montar
  useEffect(() => {
    const cargarImpuestos = async () => {
      try {
        const response = await parametrosApi.listTarifasIva();
        setImpuestosDisponibles(response.data?.data || []);
      } catch (error) {
        console.error('Error cargando impuestos:', error);
      }
    };
    cargarImpuestos();
  }, []);

  useEffect(() => {
    if (isEditing) {
      cargarProducto(id, false);
    } else if (copyFromId) {
      cargarProducto(copyFromId, true);
    }
  }, [id, copyFromId]);

  // Carga un producto existente. Si esCopia=true → vacía código (para que el user lo defina),
  // sufija el nombre con " (Copia)" y deja el resto igual. El form actúa como "Nuevo".
  const cargarProducto = async (productoId, esCopia = false) => {
    try {
      const response = await productosApi.getById(productoId);
      const data = response.data?.data;
      if (data) {
        setProducto({
          codigo: esCopia ? '' : (data.codigo || ''),
          nombre: esCopia ? `${data.nombre || ''} (Copia)` : (data.nombre || ''),
          descripcion: data.descripcion || '',
          codigoCabys: data.codigoCabys || '',
          descripcionCabys: data.descripcionCabys || '',
          unidadMedida: data.unidadMedida || 'Unid',
          moneda: data.moneda || 'CRC',
          precio: data.precio || '',
          tarifaIva: data.tarifaIva ?? 13,
          codigoTarifaIva: data.codigoTarifa || '08',
          esServicio: data.esServicio ?? data.es_servicio ?? false,
          activo: data.isActive !== false
        });
        // Cargar impuestos seleccionados
        if (data.impuestos && data.impuestos.length > 0) {
          setImpuestosSeleccionados(data.impuestos.map(imp => imp.id));
        }
        if (esCopia) {
          toast.success('Producto copiado — revise el código y nombre, luego guarde');
        }
      }
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Error al cargar producto');
      navigate('/productos');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (field, value) => {
    setProducto(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // CABYS search
  const buscarCabys = async (query) => {
    if (!query || query.length < 2) {
      setCabysResults([]);
      return;
    }

    setSearchingCabys(true);
    try {
      const response = await productosApi.searchCabys(query);
      setCabysResults(response.data?.data || []);
      setShowCabysDropdown(true);
    } catch (error) {
      console.error('Error searching CABYS:', error);
    } finally {
      setSearchingCabys(false);
    }
  };

  // Debounced CABYS search
  useEffect(() => {
    const timer = setTimeout(() => {
      buscarCabys(cabysSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [cabysSearch]);

  const seleccionarCabys = (cabys) => {
    setProducto(prev => ({
      ...prev,
      codigoCabys: cabys.codigo,
      descripcionCabys: cabys.descripcion,
      // Auto-set IVA based on CABYS if available
      tarifaIva: cabys.impuesto ?? prev.tarifaIva
    }));
    setCabysSearch('');
    setShowCabysDropdown(false);
    toast.success(`CABYS ${cabys.codigo} seleccionado`);
  };

  const limpiarCabys = () => {
    setProducto(prev => ({
      ...prev,
      codigoCabys: '',
      descripcionCabys: ''
    }));
  };

  const handleTarifaChange = (tarifa) => {
    const tarifaObj = TARIFAS_IVA.find(t => t.value === parseInt(tarifa));
    setProducto(prev => ({
      ...prev,
      tarifaIva: parseInt(tarifa),
      codigoTarifaIva: tarifaObj?.codigo || '08'
    }));
  };

  // Toggle impuesto seleccionado
  const toggleImpuesto = (impuestoId) => {
    setImpuestosSeleccionados(prev => {
      if (prev.includes(impuestoId)) {
        return prev.filter(id => id !== impuestoId);
      }
      return [...prev, impuestoId];
    });
  };

  // Agrupar impuestos por tipo
  const impuestosPorTipo = impuestosDisponibles.reduce((acc, imp) => {
    const tipo = imp.codigoImpuesto || '01';
    if (!acc[tipo]) acc[tipo] = [];
    acc[tipo].push(imp);
    return acc;
  }, {});

  const validateForm = () => {
    const newErrors = {};

    if (!producto.codigo || !producto.codigo.trim()) {
      newErrors.codigo = 'El código interno es requerido';
    }

    if (!producto.nombre) {
      newErrors.nombre = 'El nombre es requerido';
    } else if (producto.nombre.length > 200) {
      newErrors.nombre = 'Máximo 200 caracteres';
    }

    if (!producto.codigoCabys) {
      newErrors.codigoCabys = 'El código CABYS es requerido para facturación electrónica';
    }

    if (!producto.precio || parseFloat(producto.precio) <= 0) {
      newErrors.precio = 'El precio debe ser mayor a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor corrija los errores');
      return;
    }

    setLoading(true);
    try {
      const data = {
        codigo: producto.codigo || null,
        nombre: producto.nombre,
        descripcion: producto.descripcion || null,
        codigoCabys: producto.codigoCabys,
        descripcionCabys: producto.descripcionCabys || null,
        unidadMedida: producto.unidadMedida,
        moneda: producto.moneda,
        precio: parseFloat(producto.precio),
        tarifaIva: producto.tarifaIva,
        codigoImpuesto: '01',
        codigoTarifa: producto.codigoTarifaIva,
        esServicio: producto.esServicio,
        isActive: producto.activo,
        impuestoIds: impuestosSeleccionados
      };

      if (isEditing) {
        await productosApi.update(id, data);
        toast.success('Producto actualizado');
        navigate('/productos');
      } else {
        const response = await productosApi.create(data);
        toast.success('Producto creado');

        // If in modal mode, call onSuccess with the created product
        if (isModal && onSuccess) {
          const createdProduct = response.data?.data || {
            ...data,
            id: response.data?.id
          };
          onSuccess(createdProduct);
        } else {
          navigate('/productos');
        }
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.error || 'Error al guardar producto');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const handleCancel = () => {
    if (isModal && onCancel) {
      onCancel();
    } else {
      navigate('/productos');
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        {!isModal ? (
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        ) : (
          <h2 className="text-lg font-semibold text-gray-900">Crear Nuevo Producto</h2>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="producto-form"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isEditing ? 'Actualizar' : 'Guardar'}
          </button>
        </div>
      </div>

      <form id="producto-form" onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          {/* Row 1: Código interno + Nombre */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Tag className="inline h-4 w-4 mr-1" />
                Código Interno <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={producto.codigo}
                onChange={(e) => handleChange('codigo', e.target.value)}
                placeholder="SKU-001"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.codigo ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.codigo && <p className="mt-1 text-xs text-red-500">{errors.codigo}</p>}
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre del Producto/Servicio <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={producto.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                placeholder="Ej: Servicio de consultoría, Laptop HP, etc."
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.nombre ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.nombre && <p className="mt-1 text-xs text-red-500">{errors.nombre}</p>}
            </div>
          </div>

          {/* Row 2: Descripción adicional (justo después del nombre) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción adicional
            </label>
            <textarea
              value={producto.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              placeholder="Descripción detallada del producto o servicio (opcional)"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Row 3: Tipo y Estado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo
              </label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!producto.esServicio}
                    onChange={() => handleChange('esServicio', false)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Producto (Inventario)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={producto.esServicio}
                    onChange={() => handleChange('esServicio', true)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Servicio (No inventario)</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={producto.activo}
                    onChange={() => handleChange('activo', true)}
                    className="w-4 h-4 text-green-600"
                  />
                  <span className="text-sm text-gray-700">Activo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!producto.activo}
                    onChange={() => handleChange('activo', false)}
                    className="w-4 h-4 text-red-600"
                  />
                  <span className="text-sm text-gray-700">Inactivo</span>
                </label>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* Row 4: CABYS Search */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Package className="inline h-4 w-4 mr-1" />
              Código CABYS <span className="text-red-500">*</span>
            </label>

            {producto.codigoCabys ? (
              // Show selected CABYS
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded">
                      {producto.codigoCabys}
                    </span>
                  </div>
                  {producto.descripcionCabys && (
                    <p className="text-sm text-gray-600 mt-1">{producto.descripcionCabys}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={limpiarCabys}
                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              // CABYS Search
              <div className="relative">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      ref={cabysInputRef}
                      type="text"
                      value={cabysSearch}
                      onChange={(e) => setCabysSearch(e.target.value)}
                      onFocus={() => cabysResults.length > 0 && setShowCabysDropdown(true)}
                      placeholder="Buscar CABYS... Ej: 'consultoría', 'software', 'telecomunicaciones'"
                      className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.codigoCabys ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {searchingCabys && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-500 animate-spin" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCabysExplorer(true)}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-slate-500 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
                    title="Explorar catálogo CABYS"
                  >
                    <FolderTree className="h-4 w-4" />
                    <span className="hidden sm:inline">Explorar</span>
                  </button>
                </div>

                {/* CABYS Dropdown Results */}
                {showCabysDropdown && cabysResults.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {cabysResults.map((cabys, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => seleccionarCabys(cabys)}
                        className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded shrink-0">
                            {cabys.codigo}
                          </span>
                          <span className="text-sm text-gray-700 line-clamp-2">
                            {cabys.descripcion}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* No results message */}
                {showCabysDropdown && cabysSearch.length >= 3 && !searchingCabys && cabysResults.length === 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                    <p>No se encontraron códigos CABYS para "{cabysSearch}"</p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCabysDropdown(false);
                        setShowCabysExplorer(true);
                      }}
                      className="text-blue-600 hover:underline mt-2 text-sm"
                    >
                      Explorar catálogo completo
                    </button>
                  </div>
                )}
              </div>
            )}
            {errors.codigoCabys && <p className="mt-1 text-xs text-red-500">{errors.codigoCabys}</p>}
            <p className="text-xs text-gray-400 mt-1">
              Use palabras completas (ej: "servicios", "consultoria") o haga clic en "Explorar" para navegar por categorías.
            </p>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* Row 3: Unidad, Moneda, Precio, IVA, Tipo */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unidad de Medida
              </label>
              <select
                value={producto.unidadMedida}
                onChange={(e) => handleChange('unidadMedida', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {UNIDADES_MEDIDA.map(u => (
                  <option key={u.value} value={u.value}>{u.label} ({u.value})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Precio Unitario <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-1">
                <select
                  value={producto.moneda}
                  onChange={(e) => handleChange('moneda', e.target.value)}
                  className="w-20 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-gray-50"
                >
                  <option value="CRC">₡ CRC</option>
                  <option value="USD">$ USD</option>
                </select>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={producto.precio}
                  onChange={(e) => handleChange('precio', e.target.value)}
                  placeholder="0.00"
                  className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.precio ? 'border-red-500' : 'border-gray-300'}`}
                />
              </div>
              {errors.precio && <p className="mt-1 text-xs text-red-500">{errors.precio}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Impuestos Aplicables
              </label>
              <div className="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50">
                {/* IVA */}
                {impuestosPorTipo['01'] && (
                  <div>
                    <div className="text-xs font-semibold text-blue-700 mb-1">IVA</div>
                    <div className="flex flex-wrap gap-2">
                      {impuestosPorTipo['01'].map(imp => (
                        <label key={imp.id} className="flex items-center gap-1.5 cursor-pointer bg-white px-2 py-1 rounded border hover:bg-blue-50">
                          <input
                            type="checkbox"
                            checked={impuestosSeleccionados.includes(imp.id)}
                            onChange={() => toggleImpuesto(imp.id)}
                            className="rounded border-gray-300 text-blue-600"
                          />
                          <span className="text-xs">{imp.descripcion}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {/* Otros impuestos (911, Cruz Roja) */}
                {(impuestosPorTipo['07'] || impuestosPorTipo['99']) && (
                  <div>
                    <div className="text-xs font-semibold text-red-700 mb-1">Otros Impuestos</div>
                    <div className="flex flex-wrap gap-2">
                      {[...(impuestosPorTipo['07'] || []), ...(impuestosPorTipo['99'] || [])].map(imp => (
                        <label key={imp.id} className="flex items-center gap-1.5 cursor-pointer bg-white px-2 py-1 rounded border hover:bg-red-50">
                          <input
                            type="checkbox"
                            checked={impuestosSeleccionados.includes(imp.id)}
                            onChange={() => toggleImpuesto(imp.id)}
                            className="rounded border-gray-300 text-red-600"
                          />
                          <span className="text-xs">{imp.descripcion} ({imp.porcentaje}%)</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </form>

      {/* Click outside to close dropdown */}
      {showCabysDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowCabysDropdown(false)}
        />
      )}

      {/* CABYS Explorer Modal */}
      <CabysExplorer
        isOpen={showCabysExplorer}
        onClose={() => setShowCabysExplorer(false)}
        onSelect={seleccionarCabys}
      />
    </div>
  );
}
